import React from 'react'
import { Rectangle, Dot, CustomizedProps } from 'recharts'
import { useMediaQuery, useTheme } from '@mui/material'

/**
 * Lint error suppressed due to deeply nested generic types coming from Recharts not intended
 * for this level of customization.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
interface Props extends CustomizedProps<any, any> {
  /** extended by us */
  activeSegmentIndex: number | undefined
  horizontalGap: number

  /** provided by Recharts */
  isTooltipActive: boolean
  formattedGraphicalItems: {
    props: {
      points: {
        x: number
        y: number
      }[]
    }
  }[]
}

/**
 * Component used to emphasize the currently hovered horizontal line segment in the tariff chart
 * representing a window of one hour. Renders a thicker horizontal line over the existing line
 * and two dots at the edges of this line using Recharts primitives Rectangle and Dot.
 *
 * Recharts Customized component incorrectly specified return type of its "component" prop as
 * ReactElement(which wouldn't be able to accept props such as "isTooltipActive") instead of a
 * FunctionComponent accepting props. For this reason, the lint error is suppressed.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const HourSegmentIndicator = (props: Props): any => {
  const theme = useTheme()
  const fillColor = theme.palette.grey.grey00
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const DOT_OUTER_WIDTH = isMobile ? 7 : 9
  const STROKE_WIDTH = isMobile ? 2 : 2.5
  const DOT_INNER_WIDTH = DOT_OUTER_WIDTH - STROKE_WIDTH
  const { formattedGraphicalItems, isTooltipActive, horizontalGap: gap } = props

  const first = formattedGraphicalItems[0]
  const points = first?.props?.points || []

  if (points.length === 0) {
    return null
  }

  return points.map((point, index) => {
    if (props.activeSegmentIndex !== index || !isTooltipActive) {
      return null
    }

    return (
      <React.Fragment key={point.x}>
        {/* horizontal line thickening the active hour segment in the chart */}
        <Rectangle width={gap} height={STROKE_WIDTH} x={point.x} y={point.y - 1} fill={fillColor} opacity={1} />

        {/* left dot */}
        <Dot r={DOT_OUTER_WIDTH / 2} cx={point.x} cy={point.y} fill={theme.palette.background.paper} opacity={1} />
        <Dot r={DOT_INNER_WIDTH / 2} cx={point.x} cy={point.y} fill={fillColor} opacity={1} />

        {/* right dot */}
        <Dot r={DOT_OUTER_WIDTH / 2} cx={point.x + gap} cy={point.y} fill={theme.palette.background.paper} opacity={1} />
        <Dot r={DOT_INNER_WIDTH / 2} cx={point.x + gap} cy={point.y} fill={fillColor} opacity={1} />
      </React.Fragment>
    )
  })
}
